import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import { withLocale } from '@dietlabs/components';
import { reportError } from '@dietlabs/utils';

import CachedDataMessage from '@dietlabs/components/src/CachedDataMessage/CachedDataMessage';
import DayPlan from '../../components/DayPlan';
import DayPlanSummary from '../../components/DayPlanSummary';
import Meal from '../../components/DayPlanMeal';
import Training from '../../components/DayPlanTraining';
import Dish from '../../components/DayPlanDish';
import IngredientList from '../../components/DayPlanIngredientList';
import Ingredient from '../../components/DayPlanIngredient';
import IngredientReplacementList from '../../components/DayPlanIngredientReplacementList';
import IngredientReplacement from '../../components/DayPlanIngredientReplacement';
import PrintForm from '../../components/DayPlanPrintForm';

import MealReplacementList from '../../components/DayPlanMealReplacementList';
import MealReplacement from '../../components/DayPlanMealReplacement';

import DayPlanChangeDay from '../../components/DayPlanChangeDay';
import DayReplacement from '../../components/DayPlanDayReplacement';

import MealNavigation from '../../components/DayPlanMealNavigation';
import MealLink from '../../components/DayPlanMealLink';

class DayPlanContainer extends Component {
    static propTypes = {
        renderTimeline: PropTypes.func.isRequired,
        dayPlan: PropTypes.shape({
            canBeCopiedToDate: PropTypes.bool.isRequired,
            date: PropTypes.instanceOf(Date).isRequired,
            diet: PropTypes.shape({
                id: PropTypes.number.isRequired,
                kcal: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                macro: PropTypes.shape({
                    animalProtein: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                    carbohydrates: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                    fat: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                    protein: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                }).isRequired,
                originalPhase: PropTypes.shape({
                    name: PropTypes.string.isRequired,
                    colour: PropTypes.string.isRequired,
                }).isRequired,
            }),
            events: PropTypes.arrayOf(PropTypes.shape()),
            showMacros: PropTypes.bool.isRequired,
        }).isRequired,
        availableDays: PropTypes.arrayOf(PropTypes.object).isRequired,
        categories: PropTypes.arrayOf(PropTypes.shape()).isRequired,
        goal: PropTypes.shape({
            reachedBecauseOfLoseWeight: PropTypes.bool.isRequired,
            reachedBecauseOfPutOnWeight: PropTypes.bool.isRequired,
            lostBecauseOfLoseWeight: PropTypes.bool.isRequired,
            lostBecauseOfPutOnWeight: PropTypes.bool.isRequired,
        }).isRequired,
        isHolidayMenu: PropTypes.bool.isRequired,
        isTimeToNagForCurrentMeasurement: PropTypes.bool.isRequired,
        cacheHit: PropTypes.bool.isRequired,
        hasNetworkError: PropTypes.bool.isRequired,
        copyDayPlan: PropTypes.func.isRequired,
        alreadyCopied: PropTypes.bool.isRequired,
        print: PropTypes.bool,
        replaceProduct: PropTypes.func.isRequired,
        loadDayPlanMealReplacements: PropTypes.func.isRequired,
        replaceMeal: PropTypes.func.isRequired,
        loadDayPlanDayReplacements: PropTypes.func.isRequired,
        replaceDay: PropTypes.func.isRequired,
        prefetchSurroundingDays: PropTypes.func,
        loadDayPlanForTomorrow: PropTypes.func.isRequired,
        fastingStatistics: PropTypes.shape({
            dietsCount: PropTypes.number,
            currentDietLength: PropTypes.number,
            currentDietProgress: PropTypes.number,
        }).isRequired,
        fastType: PropTypes.string.isRequired,
        addMealToFavorites: PropTypes.func.isRequired,
        removeMealFromFavorites: PropTypes.func.isRequired,
        insertFavoriteToDayplan: PropTypes.func.isRequired,
        intermittentFasting: PropTypes.shape().isRequired,
    };

    static defaultProps = {
        print: false,
        prefetchSurroundingDays: undefined,
    };

    state = {
        dayPlanForTomorrow: undefined,
    };

    componentDidMount = async () => {
        const dayPlanForTomorrow = await this.props.loadDayPlanForTomorrow();
        this.setState({ dayPlanForTomorrow });

        if (!this.props.print) {
            this.props.prefetchSurroundingDays();
        }
    };

    componentDidUpdate = async () => {
        const dayPlanForTomorrow = await this.props.loadDayPlanForTomorrow();
        if (
            JSON.stringify(this.state.dayPlanForTomorrow) !==
            JSON.stringify(dayPlanForTomorrow)
        ) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ dayPlanForTomorrow });
        }

        if (!this.props.print) {
            this.props.prefetchSurroundingDays();
        }
    };

    handleLoadDayReplacements = () => {
        this.changeDayRef.loadDayReplacements(this.changeDayRef.state.search);
    };

    renderIngredientReplacements = (
        replacements,
        productAmountId,
        productId,
        productKey,
        clickAction
    ) =>
        replacements.map(replacement => (
            <IngredientReplacement
                key={replacement.productId}
                replacementId={replacement.productId}
                name={replacement.name}
                measurements={replacement.measurements}
                replaceProduct={this.props.replaceProduct}
                productId={productId}
                productKey={productKey}
                productAmountId={productAmountId}
                closeAndScroll={clickAction.closeAndScroll}
                replaceProductSuccess={() => ''}
            />
        ));

    renderIngredientReplacementsList = (
        replacements,
        originalProductId,
        productId,
        productKey,
        original,
        productAmountId,
        clickAction
    ) => (
        <IngredientReplacementList
            shouldRenderOryginalProduct={originalProductId !== productId}
            renderOriginalProduct={() =>
                this.renderIngredientReplacements(
                    [original],
                    productAmountId,
                    productId,
                    productKey,
                    clickAction
                )
            }
            hasReplacements={replacements.length > 0}
        >
            {() =>
                this.renderIngredientReplacements(
                    replacements,
                    productAmountId,
                    productId,
                    productKey,
                    clickAction
                )
            }
        </IngredientReplacementList>
    );

    renderIngredients = ingredients =>
        ingredients.map(ingredient => (
            <Ingredient
                key={ingredient.productId}
                name={ingredient.name}
                measurements={ingredient.measurements}
                saleProductVariant={ingredient.saleProductVariant}
                hasReplacement={ingredient.hasReplacement}
                originalProductId={ingredient.original.productId}
                productId={ingredient.productId}
                productKey={ingredient.key}
                print={this.props.print}
            >
                {({ closeAndScroll }) =>
                    this.renderIngredientReplacementsList(
                        ingredient.replacements,
                        ingredient.original.productId,
                        ingredient.productId,
                        ingredient.key,
                        ingredient.original,
                        ingredient.productAmountId,
                        { closeAndScroll }
                    )
                }
            </Ingredient>
        ));

    renderIngredientList = ingredients => {
        const filteredIngredients = ingredients.filter(
            ingredient =>
                !ingredient.category.isSeasoning &&
                !ingredient.category.isOptional
        );

        const onlySeasoningsIngredients = ingredients.filter(
            ingredient => ingredient.category.isSeasoning
        );

        const optionalIngredients = ingredients.filter(
            ingredient => ingredient.category.isOptional
        );

        return (
            <IngredientList
                renderIngredients={() =>
                    this.renderIngredients(filteredIngredients)
                }
                renderOnlySeasoningsIngredients={() =>
                    this.renderIngredients(onlySeasoningsIngredients)
                }
                renderOptionalIngredients={() =>
                    this.renderIngredients(optionalIngredients)
                }
            />
        );
    };

    renderDishes = dishes =>
        dishes.map(dish => (
            <Dish
                key={dish.key}
                name={dish.name}
                recipe={dish.recipe}
                recipeNote={dish.recipeNote}
                triangleOfPower={dish.triangleOfPower}
                isFirstOccurance={dish.isFirstOccurance}
                isLastOccurance={dish.isLastOccurance}
                isPortioned={dish.isPortioned}
                portions={dish.portions}
                portionsTotal={dish.portionsTotal}
                data-test="dish-component"
            >
                {() => this.renderIngredientList(dish.ingredients)}
            </Dish>
        ));

    renderMealReplacements = (
        originalMealId,
        mealKey,
        replacements,
        clickAction,
        mealName
    ) =>
        replacements.map(mealReplacement => (
            <MealReplacement
                key={mealReplacement.id}
                originalMealId={originalMealId}
                mealKey={mealKey}
                mealReplacement={mealReplacement}
                replaceMeal={this.props.replaceMeal}
                closeAndScroll={clickAction.closeAndScroll}
                replaceMealSuccess={clickAction.replaceMealSuccess}
                insertFavoriteToDayplan={this.props.insertFavoriteToDayplan}
                isFavorited={mealReplacement.isFavorited}
                dietPhase={this.props.dayPlan.diet.name}
                mealCategory={mealName}
            />
        ));

    renderMealReplacementsList = (
        replacements,
        originalMealId,
        mealId,
        mealKey,
        original,
        mealName,
        clickAction
    ) => (
        <MealReplacementList
            originalMealId={originalMealId}
            mealId={mealId}
            renderOriginalMeal={() =>
                this.renderMealReplacements(
                    originalMealId,
                    mealKey,
                    [original],
                    clickAction,
                    mealName
                )
            }
            replacements={replacements}
        >
            {() =>
                this.renderMealReplacements(
                    originalMealId,
                    mealKey,
                    replacements,
                    clickAction,
                    mealName
                )
            }
        </MealReplacementList>
    );

    renderEvents = events =>
        events.map(event => {
            if (event.__typename === 'Meal') {
                return (
                    <Meal
                        key={event.key}
                        mealId={event.id}
                        dietPhase={this.props.dayPlan.diet.name}
                        preparationTime={event.preparationTime}
                        name={event.name}
                        kcal={event.kcal}
                        macro={event.macro}
                        showMacros={this.props.dayPlan.showMacros}
                        originalId={event.original.id}
                        data-test="meal-component"
                        loadDayPlanMealReplacements={
                            this.props.loadDayPlanMealReplacements
                        }
                        renderMealReplacementsList={(
                            replacements,
                            originalMealId,
                            { closeAndScroll, replaceMealSuccess }
                        ) =>
                            this.renderMealReplacementsList(
                                replacements,
                                originalMealId,
                                event.id,
                                event.key,
                                event.original,
                                event.name,
                                { closeAndScroll, replaceMealSuccess }
                            )
                        }
                        print={this.props.print}
                        addMealToFavorites={this.props.addMealToFavorites}
                        removeMealFromFavorites={
                            this.props.removeMealFromFavorites
                        }
                        canBeAddedToFavorites={event.canBeAddedToFavorites}
                        isFavorited={event.isFavorited}
                    >
                        {() => this.renderDishes(event.dishes)}
                    </Meal>
                );
            }
            if (event.__typename === 'Training') {
                return (
                    <Training
                        key={event.__typename + event.id}
                        activities={event.activities}
                        burnedCalories={event.burnedCalories}
                        duration={event.duration}
                        data-test="training-component"
                    />
                );
            }
            reportError(
                new Error(`Unknown event typename: ${event.__typename}`)
            );
            return '';
        });

    renderMealLinks = () => (
        <MealNavigation>
            {() =>
                this.props.dayPlan.events.map(event => {
                    if (event.__typename === 'Meal') {
                        return (
                            <MealLink
                                key={event.key}
                                name={event.name}
                                mealId={event.id}
                            />
                        );
                    }
                    return '';
                })
            }
        </MealNavigation>
    );

    renderSummary = (canBeCopiedToDate, alreadyCopied) => (
        <DayPlanSummary
            canBeCopiedToDate={canBeCopiedToDate}
            copyDayPlan={this.props.copyDayPlan}
            alreadyCopied={alreadyCopied}
            print={this.props.print}
            data-test="summary-component"
        >
            {() => (
                <React.Fragment>
                    {this.renderChangeDay()}
                    {this.renderPrintForm()}
                </React.Fragment>
            )}
        </DayPlanSummary>
    );

    renderDayReplacements = (replacements, search, changeDaySuccess) =>
        replacements.map(replacement => (
            <DayReplacement
                key={replacement.dietSetId}
                dayReplacement={replacement}
                search={search}
                replaceDay={this.props.replaceDay}
                changeDaySuccess={changeDaySuccess.changeDaySuccess}
            />
        ));

    renderChangeDay = () => (
        <DayPlanChangeDay
            onRef={ref => {
                this.changeDayRef = ref;
            }}
            loadDayPlanDayReplacements={this.props.loadDayPlanDayReplacements}
            renderOriginalDay={(original, search, { changeDaySuccess }) =>
                this.renderDayReplacements([original], search, {
                    changeDaySuccess,
                })
            }
            date={this.props.dayPlan.date}
        >
            {(replacements, search, { changeDaySuccess }) =>
                this.renderDayReplacements(replacements, search, {
                    changeDaySuccess,
                })
            }
        </DayPlanChangeDay>
    );

    renderPrintForm = () => (
        <PrintForm
            date={this.props.dayPlan.date}
            availableDays={this.props.availableDays}
        />
    );

    render() {
        const {
            dayPlan,
            availableDays,
            categories,
            goal,
            isHolidayMenu,
            isTimeToNagForCurrentMeasurement,
            cacheHit,
            hasNetworkError,
            alreadyCopied,
            print,
            fastingStatistics,
        } = this.props;

        return (
            <DayPlan
                kcal={dayPlan.diet.kcal}
                date={dayPlan.date}
                name={dayPlan.diet.name}
                originalPhase={dayPlan.diet.originalPhase}
                goal={goal}
                isHolidayMenu={isHolidayMenu}
                isTimeToNagForCurrentMeasurement={
                    isTimeToNagForCurrentMeasurement
                }
                availableDays={availableDays}
                categories={categories}
                renderTimeline={position => this.props.renderTimeline(position)}
                print={print}
                dayPlanForTomorrow={this.state.dayPlanForTomorrow}
                protein={dayPlan.diet.macro.protein.percentage}
                fat={dayPlan.diet.macro.fat.percentage}
                carbohydrates={dayPlan.diet.macro.carbohydrates.percentage}
                showMacros={this.props.dayPlan.showMacros}
                renderMealLinks={() => this.renderMealLinks()}
                fastingStatistics={fastingStatistics}
                fastType={this.props.fastType}
                handleLoadDayReplacements={() =>
                    this.handleLoadDayReplacements()
                }
                intermittentFasting={this.props.intermittentFasting}
            >
                {() => (
                    <React.Fragment>
                        <CachedDataMessage {...{ cacheHit, hasNetworkError }} />
                        {this.renderEvents(dayPlan.events)}
                        {this.renderSummary(
                            dayPlan.canBeCopiedToDate,
                            alreadyCopied
                        )}
                    </React.Fragment>
                )}
            </DayPlan>
        );
    }
}

export { DayPlanContainer };
export default withRouter(withLocale(DayPlanContainer));
